import React from 'react';
import { IconMenu2, IconInbox, IconTicket } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { UserMenu } from '@/components/ui/user-menu/UserMenu';
import { GlobalSearchBar } from '@/components/global-search/GlobalSearchBar';
import { TopBarIconButton } from '@/components/ui/top-bar-icon-button';

export interface TopBarProps {
  className?: string;
  /** Opens the mobile navigation drawer. Hamburger renders only when provided. */
  onOpenMobileNav?: () => void;
  /** Renders the Inbox icon button (user has Conversations access). */
  showInbox?: boolean;
  inboxCount?: number;
  /** Already-translated Inbox label. */
  inboxLabel?: string;
  onInboxClick?: () => void;
  /** Renders the HQ Queue icon button (user's nav includes support-tickets). */
  showHqQueue?: boolean;
  hqQueueCount?: number;
  /** Already-translated HQ Queue label. */
  hqQueueLabel?: string;
  onHqQueueClick?: () => void;
  /** Assistant entry point, rendered before the inbox button. */
  assistantSlot?: React.ReactNode;
}

const TopBar = React.forwardRef<HTMLDivElement, TopBarProps>(({ className, onOpenMobileNav, showInbox = false, inboxCount = 0, inboxLabel = 'Inbox', onInboxClick, showHqQueue = false, hqQueueCount = 0, hqQueueLabel = 'HQ Queue', onHqQueueClick, assistantSlot }, ref) => {
  const { user } = useAuth();

  return (
    <header
      ref={ref}
      className={cn(
        'tee-dots-surface flex w-full items-center',
        'border-b border-[var(--color-forest-deep)]',
        'bg-[var(--color-forest)]',
        'py-[var(--spacing-xs)] px-3 md:px-[var(--spacing-xl)]',
        className,
      )}
    >
      {/* Hamburger — mobile only. The launcher previously had no navigation at
          all on a phone: it renders outside MainLayout, so it never got the
          drawer that module pages have. */}
      {onOpenMobileNav && (
        <button
          onClick={onOpenMobileNav}
          className="md:hidden flex items-center justify-center h-9 w-9 mr-1 rounded-lg hover:bg-white/10 transition-colors focus:outline-none flex-shrink-0"
          aria-label="Open navigation menu"
        >
          <IconMenu2 size={22} className="text-[var(--text-on-forest)]" />
        </button>
      )}

      <div className="flex items-center gap-[var(--spacing-l)]">
        {/* Matches ModuleTopBar's logo box exactly — this bar renders only on
            the launcher, and at 40x87 the mark read noticeably larger there
            than on every other screen. */}
        <div className="flex items-center h-8 w-14 md:w-[72px] overflow-hidden flex-shrink-0">
          <img
            src="/assets/logo-white.svg"
            alt="Tee Box Logo"
            className="block h-full w-full object-contain"
          />
        </div>
      </div>
      <div className="flex-1" />
      {user && assistantSlot && (
        <div className="mr-2 md:mr-3 flex items-center">{assistantSlot}</div>
      )}
      {user && showInbox && (
        <TopBarIconButton
          icon={<IconInbox size={22} />}
          label={inboxLabel}
          count={inboxCount}
          active={false}
          onClick={() => onInboxClick?.()}
          className="mr-2 md:mr-5"
        />
      )}
      {user && showHqQueue && (
        <TopBarIconButton
          icon={<IconTicket size={22} />}
          label={hqQueueLabel}
          count={hqQueueCount}
          active={false}
          onClick={() => onHqQueueClick?.()}
          className="mr-2 md:mr-5"
        />
      )}
      {user && (
        <div className="pr-[var(--spacing-l)] hidden md:flex">
          <GlobalSearchBar />
        </div>
      )}
      {user && <UserMenu />}
    </header>
  );
});

TopBar.displayName = 'TopBar';

export { TopBar };
